import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ErrorCode, JpegQuality, OutputFormat, QueueItem } from '../types'
import { useWorker } from './useWorker'
import { downloadBlob, downloadBlobAs } from '../lib/download'
import { buildPdfBlob } from '../lib/pdfBuilder'
import { buildZipBlob } from '../lib/zipBuilder'
import { validateFiles } from '../lib/fileValidation'
import { safeBaseName } from '../lib/safeFilename'

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

export interface RejectedFile {
  fileName: string
  code: ErrorCode
  reason: string
}

export interface UseQueueState {
  items: QueueItem[]
  format: OutputFormat
  quality: JpegQuality
  isConverting: boolean
  announcement: string
  rejected: RejectedFile[]
  addFiles: (files: FileList | null) => void
  setFormat: (format: OutputFormat) => void
  setQuality: (quality: JpegQuality) => void
  removeItem: (id: string) => void
  retryItem: (id: string) => void
  clearAll: () => void
  startConversion: () => void
  downloadItem: (id: string) => void
  downloadAll: () => Promise<void>
}

export function useQueue(initialFormat: OutputFormat = 'jpg'): UseQueueState {
  const [items, setItems] = useState<QueueItem[]>([])
  const [format, setFormatState] = useState<OutputFormat>(initialFormat)
  const [quality, setQualityState] = useState<JpegQuality>(85)
  const [isConverting, setIsConverting] = useState(false)
  const [announcement, setAnnouncement] = useState('Conversion workspace ready. No files selected.')
  const [rejected, setRejected] = useState<RejectedFile[]>([])
  const itemsRef = useRef(items)
  itemsRef.current = items

  const { start: workerStart, cancel: workerCancel, terminate } = useWorker()

  // Stable announcement helper that never appears in callback dependencies.
  const announceRef = useRef((text: string) => setAnnouncement(text))

  const updateItem = useCallback(
    (
      id: string,
      patch: Partial<QueueItem>,
      opts: { announce?: string } = {},
    ) => {
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)))
      if (opts.announce) {
        setAnnouncement(opts.announce)
      }
    },
    [],
  )

  const addFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return
      const { accepted, rejected: rejectedFiles } = validateFiles(
        files,
        itemsRef.current.length,
      )
      if (rejectedFiles.length > 0) {
        setRejected((prev) => [...prev, ...rejectedFiles])
        const summary = rejectedFiles.map((r) => `${r.fileName}: ${r.reason}`).join('; ')
        announceRef.current(`Rejected ${rejectedFiles.length} file(s). ${summary}`)
      }
      if (accepted.length === 0) return

      const newItems: QueueItem[] = accepted.map((file) => ({
        id: generateId(),
        file,
        displayName: safeBaseName(file.name),
        size: file.size,
        status: 'waiting',
        outputFormat: format,
        jpegQuality: quality,
      }))

      setItems((prev) => [...prev, ...newItems])
      announceRef.current(
        `Added ${newItems.length} file(s). ${newItems.length} waiting, ${itemsRef.current.length + newItems.length} total.`,
      )
    },
    [format, quality],
  )

  const setFormat = useCallback(
    (nextFormat: OutputFormat) => {
      setFormatState(nextFormat)
      setItems((prev) =>
        prev.map((it) =>
          it.status === 'waiting' ? { ...it, outputFormat: nextFormat } : it,
        ),
      )
      announceRef.current(`Output format set to ${nextFormat.toUpperCase()}.`)
    },
    [],
  )

  const setQuality = useCallback(
    (nextQuality: JpegQuality) => {
      setQualityState(nextQuality)
      setItems((prev) =>
        prev.map((it) =>
          it.status === 'waiting' && it.outputFormat === 'jpg'
            ? { ...it, jpegQuality: nextQuality }
            : it,
        ),
      )
      announceRef.current(`JPG quality set to ${nextQuality}.`)
    },
    [],
  )

  const removeItem = useCallback(
    (id: string) => {
      const item = itemsRef.current.find((it) => it.id === id)
      if (!item) return
      if (item.status === 'converting') {
        workerCancel(id)
        updateItem(id, { status: 'cancelled' }, { announce: `Cancelled ${item.displayName}.` })
      } else {
        setItems((prev) => prev.filter((it) => it.id !== id))
        announceRef.current(`Removed ${item.displayName}.`)
      }
    },
    [workerCancel, updateItem],
  )

  const retryItem = useCallback(
    (id: string) => {
      const item = itemsRef.current.find((it) => it.id === id)
      if (!item || item.status === 'converting') return
      updateItem(
        id,
        { status: 'waiting', errorCode: undefined, progressPhase: undefined },
        { announce: `Retrying ${item.displayName}.` },
      )
    },
    [updateItem],
  )

  const clearAll = useCallback(() => {
    terminate()
    setItems([])
    setRejected([])
    setIsConverting(false)
    announceRef.current('All files removed.')
  }, [terminate])

  const processNext = useCallback(async () => {
    const next = itemsRef.current.find((it) => it.status === 'waiting')
    if (!next) {
      setIsConverting(false)
      return
    }
    const id = next.id
    updateItem(id, { status: 'converting', progressPhase: 'decode' }, {
      announce: `Converting ${next.displayName}.`,
    })

    try {
      const buffer = await next.file.arrayBuffer()
      const result = await workerStart(
        id,
        buffer,
        next.file.type,
        next.outputFormat,
        next.jpegQuality,
        (phase) => updateItem(id, { progressPhase: phase }),
      )

      const current = itemsRef.current.find((it) => it.id === id)
      if (!current || current.status !== 'converting') {
        // Item was removed or cancelled while converting; continue with the rest of the queue.
        void processNext()
        return
      }

      if (result.status === 'succeeded' && result.blob) {
        updateItem(
          id,
          { status: 'succeeded', result: result.blob, progressPhase: undefined },
          { announce: `${current.displayName} converted. Ready to download.` },
        )
      } else {
        updateItem(
          id,
          { status: 'failed', errorCode: result.code ?? 'unknown', progressPhase: undefined },
          { announce: `${current.displayName} failed. ${result.code ?? 'unknown'}.` },
        )
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Conversion error', error)
      const current = itemsRef.current.find((it) => it.id === id)
      if (current && current.status === 'converting') {
        updateItem(id, { status: 'failed', errorCode: 'unknown', progressPhase: undefined }, {
          announce: `${current.displayName} failed.`,
        })
      }
    }

    // Intentionally not awaited; keeps the queue moving without awaiting UI paint.
    void processNext()
  }, [workerStart, updateItem])

  const startConversion = useCallback(() => {
    if (isConverting) return
    const hasWaiting = itemsRef.current.some((it) => it.status === 'waiting')
    if (!hasWaiting) return
    setIsConverting(true)
    void processNext()
  }, [isConverting, processNext])

  const downloadItem = useCallback(
    async (id: string) => {
      const item = itemsRef.current.find((it) => it.id === id)
      if (!item || !item.result) return
      const blob = item.outputFormat === 'pdf'
        ? await buildPdfBlob([{ fileName: item.displayName, blob: item.result }])
        : item.result
      downloadBlob({
        blob,
        fileName: item.displayName,
        outputFormat: item.outputFormat,
      })
      announceRef.current(`Downloaded ${item.displayName}.`)
    },
    [],
  )

  const downloadAll = useCallback(async () => {
    const succeeded = itemsRef.current.filter((it) => it.status === 'succeeded' && it.result)
    if (succeeded.length === 0) return
    if (succeeded.length === 1) {
      await downloadItem(succeeded[0].id)
      return
    }

    const outputFormat = succeeded[0].outputFormat
    const entries = succeeded.map((it) => ({
      fileName: it.displayName,
      blob: it.result!,
    }))

    try {
      if (outputFormat === 'pdf') {
        const blob = await buildPdfBlob(entries)
        downloadBlob({ blob, fileName: 'heic-converted', outputFormat: 'pdf' })
      } else {
        const blob = await buildZipBlob(entries, outputFormat)
        downloadBlobAs(blob, 'heic-converted', 'zip')
      }
      announceRef.current('Batch download started.')
    } catch (_error) {
      // eslint-disable-next-line no-console
      console.error('Batch download error', _error)
      announceRef.current('Batch download failed. Try fewer files or a smaller image size.')
    }
  }, [downloadItem])

  useEffect(() => {
    const handleBeforeUnload = () => {
      terminate()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      terminate()
    }
  }, [terminate])

  const counts = useMemo(() => {
    const total = items.length
    const waiting = items.filter((it) => it.status === 'waiting').length
    const succeeded = items.filter((it) => it.status === 'succeeded').length
    const failed = items.filter((it) => it.status === 'failed').length
    return { total, waiting, succeeded, failed }
  }, [items])

  // Re-announce status when the queue reaches a terminal state.
  useEffect(() => {
    if (counts.total === 0) return
    if (counts.succeeded + counts.failed === counts.total && !isConverting) {
      announceRef.current(`${counts.succeeded} file(s) succeeded, ${counts.failed} failed.`)
    }
  }, [counts, isConverting])

  return {
    items,
    format,
    quality,
    isConverting,
    announcement,
    rejected,
    addFiles,
    setFormat,
    setQuality,
    removeItem,
    retryItem,
    clearAll,
    startConversion,
    downloadItem,
    downloadAll,
  }
}
